/**
 * Flat logical topology (Cisco-style): tiers top to bottom, one column per
 * room, passive cabling collapsed to a bar at the foot of each column.
 * Pan by dragging, zoom with the wheel or the buttons; click a device to
 * select it. Layout comes from `buildTopology` — this file only draws.
 */
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { kindColor } from '../../lib/deviceColors';
import { CONFIDENCE_LABEL, MEDIUM_LABEL, networkColor, type RackPlant } from '../../lib/rackModel';
import { buildTopology, type NodeIcon, type TopoEdge, type TopoNode } from '../../lib/logicalTopology';

const LEFT = 96; // tier label gutter

export function LogicalView({
  plant, networkId, selectedUnitId, onSelectUnit, onSelectRoom, rightInset = 0,
}: {
  plant: RackPlant;
  networkId: string | null;
  selectedUnitId: string | null;
  onSelectUnit: (unitId: string) => void;
  onSelectRoom: (roomId: string) => void;
  /** Width covered by an overlay on the right (the floating network panel). */
  rightInset?: number;
}) {
  const topo = useMemo(() => buildTopology(plant, networkId), [plant, networkId]);
  const network = networkId ? plant.networkById.get(networkId) : undefined;
  const hostRef = useRef<HTMLDivElement>(null);
  const [view, setView] = useState({ x: 0, y: 0, k: 1 });
  const [hoverEdge, setHoverEdge] = useState<string | null>(null);
  const drag = useRef<{ x: number; y: number; vx: number; vy: number; moved: boolean } | null>(null);

  const contentW = topo.width + LEFT + 24;
  const contentH = topo.height + 24;

  const fit = () => {
    const host = hostRef.current;
    if (!host) return;
    const w = Math.max(host.clientWidth - rightInset, 200);
    const top = 48; // clear the toolbar
    const k = Math.min(w / contentW, (host.clientHeight - top - 48) / contentH, 1.4);
    setView({ k, x: (w - contentW * k) / 2, y: top + Math.max(0, (host.clientHeight - top - 48 - contentH * k) / 2) });
  };
  useLayoutEffect(fit, [topo, rightInset]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    const ro = new ResizeObserver(() => fit());
    ro.observe(host);
    return () => ro.disconnect();
  }, [topo]); // eslint-disable-line react-hooks/exhaustive-deps

  // Wheel zoom around the cursor (non-passive so the page does not scroll).
  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const rect = host.getBoundingClientRect();
      const px = e.clientX - rect.left;
      const py = e.clientY - rect.top;
      setView((v) => {
        const k = Math.min(3, Math.max(0.25, v.k * Math.exp(-e.deltaY * 0.0015)));
        return { k, x: px - ((px - v.x) * k) / v.k, y: py - ((py - v.y) * k) / v.k };
      });
    };
    host.addEventListener('wheel', onWheel, { passive: false });
    return () => host.removeEventListener('wheel', onWheel);
  }, []);

  const zoom = (f: number) => {
    const host = hostRef.current;
    if (!host) return;
    const cx = host.clientWidth / 2;
    const cy = host.clientHeight / 2;
    setView((v) => {
      const k = Math.min(3, Math.max(0.25, v.k * f));
      return { k, x: cx - ((cx - v.x) * k) / v.k, y: cy - ((cy - v.y) * k) / v.k };
    });
  };

  const nodeById = useMemo(() => new Map(topo.nodes.map((n) => [n.id, n])), [topo]);
  const selectedNodeId = useMemo(
    () => (selectedUnitId ? topo.nodes.find((n) => n.unitIds.includes(selectedUnitId))?.id ?? null : null),
    [topo, selectedUnitId],
  );
  const litEdges = useMemo(() => {
    if (!selectedNodeId) return null;
    return new Set(topo.edges.filter((e) => e.from === selectedNodeId || e.to === selectedNodeId).map((e) => e.id));
  }, [topo, selectedNodeId]);
  const litNodes = useMemo(() => {
    if (!litEdges) return null;
    const s = new Set<string>([selectedNodeId!]);
    for (const e of topo.edges) if (litEdges.has(e.id)) { s.add(e.from); s.add(e.to); }
    return s;
  }, [topo, litEdges, selectedNodeId]);

  const hovered = hoverEdge ? topo.edges.find((e) => e.id === hoverEdge) : undefined;

  if (topo.nodes.length === 0) {
    return (
      <div className="flex h-full items-center justify-center p-6 text-center text-sm text-text-muted">
        {network ? `No devices recorded for ${network.name}.` : 'No active devices recorded for this site.'}
      </div>
    );
  }

  return (
    <div
      ref={hostRef}
      className="relative h-full w-full cursor-grab touch-none select-none overflow-hidden active:cursor-grabbing"
      data-testid="logical-view"
      onPointerDown={(e) => {
        drag.current = { x: e.clientX, y: e.clientY, vx: view.x, vy: view.y, moved: false };
        (e.target as Element).setPointerCapture?.(e.pointerId);
      }}
      onPointerMove={(e) => {
        const d = drag.current;
        if (!d) return;
        const dx = e.clientX - d.x;
        const dy = e.clientY - d.y;
        if (Math.abs(dx) + Math.abs(dy) > 3) d.moved = true;
        if (d.moved) setView((v) => ({ ...v, x: d.vx + dx, y: d.vy + dy }));
      }}
      onPointerUp={() => { setTimeout(() => { drag.current = null; }, 0); }}
    >
      <svg width="100%" height="100%" role="img" aria-label="Logical topology">
        <defs>
          <marker id="topo-dot" viewBox="0 0 6 6" refX="3" refY="3" markerWidth="5" markerHeight="5">
            <circle cx="3" cy="3" r="2.4" fill="var(--text-muted)" />
          </marker>
        </defs>
        <g transform={`translate(${view.x} ${view.y}) scale(${view.k})`}>
          {/* Tier bands */}
          {topo.rows.map((r, i) => (
            <g key={r.label}>
              {i % 2 === 0 && <rect x={0} y={r.y - 12} width={contentW} height={r.h + 24} fill="var(--surface-2)" opacity={0.55} />}
              <text x={10} y={r.y + Math.min(r.h, 76) / 2 + 4} fontSize={10.5} fontWeight={600} letterSpacing="0.08em" fill="var(--text-muted)" className="uppercase">
                {r.label}
              </text>
            </g>
          ))}

          <g transform={`translate(${LEFT} 0)`}>
            {/* Room columns */}
            {topo.columns.map((c) => (
              <g key={c.roomId} className="cursor-pointer" onClick={() => !drag.current?.moved && onSelectRoom(c.roomId)}>
                <rect x={c.x} y={4} width={c.w} height={topo.height - 4} rx={10} fill="none" stroke="var(--border)" strokeDasharray="4 4" />
                <text x={c.x + 12} y={26} fontSize={12.5} fontWeight={700} fill="var(--text)">{c.title}</text>
              </g>
            ))}

            {/* Edges */}
            {topo.edges.map((e) => (
              <EdgePath
                key={e.id}
                edge={e}
                a={nodeById.get(e.from)!}
                b={nodeById.get(e.to)!}
                dim={litEdges ? !litEdges.has(e.id) : false}
                lit={litEdges?.has(e.id) ?? false}
                tint={network ? networkColor(network) : null}
                onHover={setHoverEdge}
              />
            ))}

            {/* Nodes */}
            {topo.nodes.map((n) => (
              <NodeView
                key={n.id}
                node={n}
                selected={n.id === selectedNodeId}
                dim={litNodes ? !litNodes.has(n.id) : false}
                tint={network ? networkColor(network) : null}
                onClick={() => {
                  if (drag.current?.moved) return;
                  if (n.unitId) onSelectUnit(n.unitId);
                  else onSelectRoom(n.roomId);
                }}
              />
            ))}
          </g>
        </g>
      </svg>

      <div className="absolute bottom-3 left-3 flex gap-1.5">
        <ZoomButton label="Zoom out" onClick={() => zoom(1 / 1.25)}>−</ZoomButton>
        <ZoomButton label="Zoom in" onClick={() => zoom(1.25)}>+</ZoomButton>
        <ZoomButton label="Fit" onClick={fit}>Fit</ZoomButton>
      </div>
      {topo.hidden > 0 && network && (
        <div className="absolute bottom-3 right-3 rounded-sm border border-border bg-bg-elevated px-2 py-1 text-xs text-text-muted">
          {network.name} only · {topo.hidden} other device{topo.hidden === 1 ? '' : 's'} hidden
        </div>
      )}
      {hovered && (
        <div className="pointer-events-none absolute left-1/2 top-3 max-w-md -translate-x-1/2 rounded-sm border border-border bg-bg-elevated px-3 py-2 text-xs shadow">
          {hovered.links.map((l) => (
            <div key={l.id} className="text-text">
              <span className="font-medium">{l.label ?? MEDIUM_LABEL[l.medium]}</span>
              <span className="text-text-muted"> · {MEDIUM_LABEL[l.medium]} · {CONFIDENCE_LABEL[l.confidence]}</span>
              {l.note && <div className="text-text-muted">{l.note}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ZoomButton({ label, onClick, children }: { label: string; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onPointerDown={(e) => e.stopPropagation()}
      onClick={onClick}
      className="h-8 min-w-8 rounded-sm border border-border bg-bg-elevated px-2 text-sm font-medium text-text hover:bg-surface-3"
    >
      {children}
    </button>
  );
}

function edgeColor(medium: string) {
  return medium.startsWith('fiber') ? 'var(--accent-orange)' : medium === 'copper' ? 'var(--info)' : 'var(--accent-purple)';
}

function EdgePath({ edge, a, b, dim, lit, tint, onHover }: {
  edge: TopoEdge; a: TopoNode; b: TopoNode; dim: boolean; lit: boolean; tint: string | null; onHover: (id: string | null) => void;
}) {
  // Orthogonal route: leave the lower node's top, the upper node's bottom.
  const [top, bottom] = a.y <= b.y ? [a, b] : [b, a];
  const sameRow = Math.abs(a.y - b.y) < 1;
  let d: string;
  if (sameRow) {
    const [l, r] = a.x <= b.x ? [a, b] : [b, a];
    const y = l.y + l.h / 2;
    d = `M ${l.x + l.w} ${y} H ${r.x}`;
    if (l.kind === 'cabling') {
      const y2 = l.y + l.h + 16;
      d = `M ${l.x + l.w / 2} ${l.y + l.h} V ${y2} H ${r.x + r.w / 2} V ${r.y + r.h}`;
    }
  } else {
    const x1 = top.x + top.w / 2;
    const y1 = top.y + top.h;
    const x2 = bottom.x + bottom.w / 2;
    const y2 = bottom.y;
    const mid = y1 + Math.max(14, (y2 - y1) / 2);
    d = `M ${x1} ${y1} V ${mid} H ${x2} V ${y2}`;
  }
  const primary = edge.links[0];
  const verified = edge.links.every((l) => l.confidence === 'verified');
  const contested = edge.links.some((l) => l.confidence === 'contested');
  const color = tint && primary.medium === 'logical' ? tint : edgeColor(primary.medium);
  return (
    <g onPointerEnter={() => onHover(edge.id)} onPointerLeave={() => onHover(null)} opacity={dim ? 0.15 : 1}>
      <path d={d} fill="none" stroke="transparent" strokeWidth={12} />
      <path
        d={d}
        fill="none"
        stroke={contested ? 'var(--warning)' : color}
        strokeWidth={lit ? 3.2 : 2}
        strokeDasharray={verified ? undefined : contested ? '2 4' : '6 4'}
        strokeLinejoin="round"
        markerStart="url(#topo-dot)"
        markerEnd="url(#topo-dot)"
      />
    </g>
  );
}

function NodeView({ node, selected, dim, tint, onClick }: {
  node: TopoNode; selected: boolean; dim: boolean; tint: string | null; onClick: () => void;
}) {
  if (node.kind === 'cabling') {
    return (
      <g opacity={dim ? 0.3 : 1} onClick={onClick} className="cursor-pointer">
        <rect x={node.x} y={node.y} width={node.w} height={node.h} rx={node.h / 2} fill="var(--surface-3)" stroke="var(--border)" />
        <text x={node.x + node.w / 2} y={node.y + node.h / 2 + 4} textAnchor="middle" fontSize={10.5} fill="var(--text-muted)">
          {node.label} · {node.sub}
        </text>
      </g>
    );
  }
  const stroke = selected ? 'var(--primary)' : node.kind === 'presence' ? (tint ?? 'var(--text-muted)') : 'var(--border)';
  return (
    <g
      opacity={dim ? 0.3 : 1}
      onClick={onClick}
      className="cursor-pointer"
      role="button"
      aria-label={node.label}
    >
      <rect
        x={node.x}
        y={node.y}
        width={node.w}
        height={node.h}
        rx={8}
        fill={node.kind === 'presence' ? 'transparent' : 'var(--bg-elevated)'}
        stroke={stroke}
        strokeWidth={selected ? 2.5 : 1}
        strokeDasharray={node.kind === 'presence' ? '5 4' : undefined}
      />
      {tint && node.kind === 'device' && <rect x={node.x} y={node.y} width={4} height={node.h} rx={2} fill={tint} />}
      {node.icon && (
        <g transform={`translate(${node.x + node.w / 2 - 16} ${node.y + 6})`}>
          <CiscoIcon icon={node.icon} />
        </g>
      )}
      <text x={node.x + node.w / 2} y={node.y + (node.icon ? 50 : 34)} textAnchor="middle" fontSize={10.5} fontWeight={600} fill="var(--text)">
        {clip(node.label, 20)}
      </text>
      <text x={node.x + node.w / 2} y={node.y + (node.icon ? 64 : 50)} textAnchor="middle" fontSize={9} fill="var(--text-muted)" className="font-mono">
        {node.sub}
      </text>
    </g>
  );
}

/** Flat renditions of the standard Cisco network icons, 32×32. */
function CiscoIcon({ icon }: { icon: NodeIcon }) {
  const fill = kindColor('router');
  const ink = 'var(--text-inverse)';
  switch (icon) {
    case 'router':
      return (
        <g>
          <ellipse cx="16" cy="20" rx="15" ry="8" fill={fill} opacity={0.8} />
          <ellipse cx="16" cy="14" rx="15" ry="8" fill={fill} />
          <path d="M9 14h14M16 9v10M9 14l3-2M9 14l3 2M23 14l-3-2M23 14l-3 2" stroke={ink} strokeWidth={1.6} fill="none" strokeLinecap="round" />
        </g>
      );
    case 'firewall':
      return (
        <g>
          <rect x="2" y="5" width="28" height="22" rx="2" fill={kindColor('firewall')} />
          <path d="M2 12h28M2 19h28M10 5v7M22 5v7M16 12v7M6 19v8M26 19v8M16 19v8" stroke={ink} strokeWidth={1.3} />
        </g>
      );
    case 'gateway':
      return (
        <g>
          <rect x="2" y="7" width="28" height="18" rx="3" fill={kindColor('gateway')} />
          <path d="M8 16h14M18 12l4 4-4 4" stroke={ink} strokeWidth={1.8} fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </g>
      );
    case 'core':
      return (
        <g>
          <rect x="1" y="6" width="30" height="20" rx="2" fill={kindColor('switch')} />
          <rect x="1" y="6" width="30" height="5" fill="var(--primary-active)" opacity={0.35} />
          <path d="M6 16h8M6 16l2.5-2.5M6 16l2.5 2.5M26 16h-8M26 16l-2.5-2.5M26 16l-2.5 2.5M16 13v8" stroke={ink} strokeWidth={1.6} fill="none" strokeLinecap="round" />
        </g>
      );
    case 'switch':
      return (
        <g>
          <rect x="2" y="8" width="28" height="16" rx="2" fill={kindColor('switch')} />
          <path d="M7 13h9M16 13l-2-2M16 13l-2 2M25 19h-9M16 19l2-2M16 19l2 2" stroke={ink} strokeWidth={1.6} fill="none" strokeLinecap="round" />
        </g>
      );
    case 'server':
      return (
        <g>
          <rect x="8" y="2" width="16" height="28" rx="2" fill={kindColor('server')} />
          <path d="M11 8h10M11 12h10M11 16h10" stroke={ink} strokeWidth={1.4} />
          <circle cx="16" cy="24" r="1.8" fill={ink} />
        </g>
      );
    case 'phone':
      return (
        <g>
          <rect x="3" y="8" width="26" height="18" rx="3" fill={kindColor('phone_system')} />
          <path d="M9 15c2-4 12-4 14 0l-2 2c-1-1-2-1-3 0h-4c-1-1-2-1-3 0z" fill={ink} />
        </g>
      );
    default:
      return (
        <g>
          <rect x="5" y="6" width="22" height="15" rx="2" fill="var(--text-muted)" />
          <rect x="11" y="23" width="10" height="3" fill="var(--text-muted)" />
        </g>
      );
  }
}

function clip(s: string, n: number) {
  return s.length > n ? `${s.slice(0, n - 1)}…` : s;
}
